import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Calendar as CalIcon, MapPin, User, Clock, X, Hash, Home, FileText, StickyNote } from 'lucide-react';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
  const day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1; // Monday = 0
}

export default function CalendarPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [detailEvent, setDetailEvent] = useState(null);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const startDate = `${year}-${String(month + 1).padStart(2, '0')}-01`;
      const endDate = `${year}-${String(month + 1).padStart(2, '0')}-${getDaysInMonth(year, month)}`;
      const { data } = await api.get(`/calendar/events?start=${startDate}&end=${endDate}`);
      setEvents(data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api, year, month]);

  useEffect(() => { load(); }, [load]);

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const goToday = () => setCurrentDate(new Date());

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  const eventsByDate = useMemo(() => {
    const map = {};
    events.forEach(e => {
      if (e.date) {
        if (!map[e.date]) map[e.date] = [];
        map[e.date].push(e);
      }
    });
    return map;
  }, [events]);

  const selectedEvents = selectedDate ? (eventsByDate[selectedDate] || []) : [];

  const calendarDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(d);
    return days;
  }, [firstDay, daysInMonth]);

  return (
    <div data-testid="calendar-page" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope]">Calendar</h1>
          <p className="text-sm text-slate-500 mt-1 font-medium">Appraisal scheduling & follow-ups · <span className="font-mono-num font-bold text-slate-700">{events.length}</span> events this month</p>
        </div>
        <button onClick={goToday} className="px-3.5 py-1.5 border border-[#E2E2DF] rounded-md text-sm font-semibold text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-colors">Today</button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Calendar Grid */}
        <div className="lg:col-span-3 bg-white border border-[#E2E2DF] rounded-md overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#E2E2DF]">
            <button data-testid="calendar-prev" onClick={prevMonth} className="p-2 rounded-md hover:bg-slate-100 transition-colors"><ChevronLeft size={20} className="text-slate-600" /></button>
            <h2 className="text-lg font-bold text-slate-900 font-[Manrope]">{MONTHS[month]} {year}</h2>
            <button data-testid="calendar-next" onClick={nextMonth} className="p-2 rounded-md hover:bg-slate-100 transition-colors"><ChevronRight size={20} className="text-slate-600" /></button>
          </div>
          <div className="grid grid-cols-7">
            {DAYS.map(d => <div key={d} className="px-2 py-2 text-center text-xs font-bold uppercase tracking-wider text-slate-500 border-b border-[#E2E2DF] bg-slate-50">{d}</div>)}
            {calendarDays.map((day, idx) => {
              if (day === null) return <div key={`empty-${idx}`} className="min-h-[90px] border-b border-r border-[#E2E2DF] bg-slate-50/30" />;
              const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const dayEvents = eventsByDate[dateStr] || [];
              const isToday = dateStr === todayStr;
              const isSelected = dateStr === selectedDate;
              return (
                <div key={dateStr} data-testid={`calendar-day-${dateStr}`} onClick={() => setSelectedDate(dateStr)} className={`min-h-[90px] border-b border-r border-[#E2E2DF] p-1.5 cursor-pointer transition-all hover:bg-slate-50 ${isSelected ? 'bg-[#D35400]/5 ring-1 ring-inset ring-[#D35400]' : ''}`}>
                  <div className={`text-xs font-semibold mb-1 ${isToday ? 'w-6 h-6 rounded-full bg-[#D35400] text-white flex items-center justify-center' : 'text-slate-700 pl-1'}`}>{day}</div>
                  <div className="space-y-0.5">
                    {dayEvents.slice(0, 3).map((ev, i) => (
                      <div key={i}
                        data-testid={`calendar-event-${ev._id || i}`}
                        className="text-[10px] px-1.5 py-0.5 rounded truncate font-medium cursor-pointer"
                        style={{ backgroundColor: `${ev.color}15`, color: ev.color, borderLeft: `2px solid ${ev.color}` }}
                        onClick={e => {
                          e.stopPropagation();
                          if (ev.type === 'calendar_booking') {
                            setDetailEvent(ev);
                          } else if (ev.type === 'appraisal') {
                            navigate(`/appraisals/${ev._id}`);
                          }
                        }}>
                        {ev.title}
                      </div>
                    ))}
                    {dayEvents.length > 3 && <div className="text-[10px] text-slate-400 pl-1">+{dayEvents.length - 3} more</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {/* Side Panel */}
        <div className="bg-white border border-[#E2E2DF] rounded-md p-4">
          <h3 className="text-sm font-bold text-slate-800 font-[Manrope] mb-3">
            {selectedDate ? new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-NZ', { weekday: 'long', day: 'numeric', month: 'long' }) : 'Select a date'}
          </h3>
          {selectedDate && selectedEvents.length === 0 && <p className="text-xs text-slate-400 py-4">No events on this day</p>}
          <div className="space-y-3">
            {selectedEvents.map((ev, i) => (
              <div key={i}
                data-testid={`calendar-side-event-${ev._id || i}`}
                className="p-3 rounded-md border border-[#E2E2DF] hover:border-slate-300 cursor-pointer transition-all"
                onClick={() => {
                  if (ev.type === 'calendar_booking') {
                    setDetailEvent(ev);
                  } else if (ev.type === 'appraisal') {
                    navigate(`/appraisals/${ev._id}`);
                  }
                }}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: ev.color }} />
                  <span className="text-xs font-bold" style={{ color: ev.color }}>{ev.status}</span>
                </div>
                <p className="text-sm font-semibold text-slate-800">{ev.title}</p>
                {ev.detail?.property_address && <p className="text-xs text-slate-500 flex items-center gap-1 mt-1"><MapPin size={10} />{ev.detail.property_address}</p>}
                {ev.contractor && <p className="text-xs text-slate-500 flex items-center gap-1"><User size={10} />{ev.contractor}</p>}
                {ev.end_date && ev.end_date !== ev.date && <p className="text-xs text-slate-500 flex items-center gap-1"><Clock size={10} />{ev.date} → {ev.end_date}</p>}
              </div>
            ))}
          </div>
          {!selectedDate && (
            <div className="space-y-2 mt-4">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Legend</p>
              {[['Booked', '#7C3AED'], ['Completed', '#059669'], ['Quoted', '#D97706'], ['Accepted', '#2563EB'], ['In Progress', '#0D9488'], ['Follow-up', '#F59E0B']].map(([label, color]) => (
                <div key={label} className="flex items-center gap-2 text-xs text-slate-600"><div className="w-3 h-3 rounded" style={{ backgroundColor: `${color}20`, border: `1px solid ${color}` }} />{label}</div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* iter303ak-fix30 · Booking detail popover — Trevor's spec 3a fields. */}
      {detailEvent && (
        <div
          data-testid="calendar-detail-popover"
          className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4"
          onClick={() => setDetailEvent(null)}>
          <div
            className="bg-white rounded-lg shadow-2xl max-w-lg w-full p-5"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                  {detailEvent.status} · calendar booking
                </div>
                <div className="text-lg font-semibold text-slate-900 mt-1 font-[Manrope]">
                  {detailEvent.title}
                </div>
              </div>
              <button
                data-testid="calendar-detail-close"
                onClick={() => setDetailEvent(null)}
                className="p-1 hover:bg-slate-100 rounded">
                <X size={16} className="text-slate-500" />
              </button>
            </div>
            {detailEvent.detail && (
              <div className="space-y-2 text-sm text-slate-700">
                <div className="flex items-start gap-2">
                  <User size={14} className="text-slate-400 mt-0.5 shrink-0" />
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Client</div>
                    <div>{detailEvent.detail.client_name || '—'} <span className="text-slate-400">({detailEvent.detail.client_id || '—'})</span></div>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Home size={14} className="text-slate-400 mt-0.5 shrink-0" />
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Property</div>
                    <div>{detailEvent.detail.property_address || '—'}</div>
                    <div className="text-[10px] text-slate-400">ID: {detailEvent.detail.property_id || '—'}</div>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <FileText size={14} className="text-slate-400 mt-0.5 shrink-0" />
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Quote</div>
                    <div>{detailEvent.detail.quote_type || '—'}{detailEvent.detail.variant_label ? ` · ${detailEvent.detail.variant_label}` : ''}</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">
                      Ref A{detailEvent.detail.parent_appraisal_no || '?'}
                      {detailEvent.detail.child_appraisal_id ? ` · child ${String(detailEvent.detail.child_appraisal_id).slice(-6)}` : ''}
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Clock size={14} className="text-slate-400 mt-0.5 shrink-0" />
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Expected work time</div>
                    <div>
                      <b className="font-mono">{detailEvent.detail.expected_work_hours ? `${detailEvent.detail.expected_work_hours} hrs` : `${detailEvent.detail.expected_work_days} days`}</b>
                      <span className="text-slate-400"> · {detailEvent.detail.scheduled_start} → {detailEvent.detail.scheduled_end}</span>
                    </div>
                  </div>
                </div>
                {detailEvent.detail.notes && (
                  <div className="flex items-start gap-2">
                    <StickyNote size={14} className="text-slate-400 mt-0.5 shrink-0" />
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Notes</div>
                      <div>{detailEvent.detail.notes}</div>
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="flex items-center justify-end gap-2 pt-4 mt-4 border-t border-slate-200">
              {detailEvent.detail?.parent_appraisal_id && (
                <button
                  data-testid="calendar-detail-open-appraisal"
                  onClick={() => navigate(`/appraisals/${detailEvent.detail.parent_appraisal_id}`)}
                  className="px-3 py-1.5 text-xs font-semibold text-slate-700 bg-white border border-slate-300 rounded hover:bg-slate-50 inline-flex items-center gap-1">
                  <Hash size={12} /> Open appraisal
                </button>
              )}
              <button
                data-testid="calendar-detail-dismiss"
                onClick={() => setDetailEvent(null)}
                className="px-3 py-1.5 text-xs font-semibold text-white bg-slate-700 rounded hover:bg-slate-800">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
